<template>
  <ValidationObserver ref="wizardPage4" id="wizardPage4" name="wizardPage4">

    <table class="table table-bordered">
      <tr>
        <td width="50%">
          <h4 class="font-weight-bold">
            Current Fleet <span v-if="!fleetLookupRunning && !fleetExtractionRunning && appData.fleetInfo.fleetCount > 0"> Size: <strong>{{ appData.fleetInfo.fleetCount }}</strong></span>
          </h4>
          <div v-if="fleetExtractionRunning" class="text-muted">
            Please wait for the fleet data extraction to be completed.
          </div>
          <b-alert v-if="showDeclarationFleetLoadedBanner" variant="info" show class="mt-3 mb-0">
            Fleet from the renewal declaration has been loaded automatically.
          </b-alert>

          <div>
            <button v-if="!fleetExtractionRunning && !fleetLookupRunning && appData.fleetInfo.fleetCount > 0" class="btn btn-primary btn-sm mr-5" @click.prevent="downloadTransactionFleet"
                    :disabled="appData.fleetInfo.downloadingTransactionFleet">
              <i class="fa fa-download fa-sm"></i> Download Fleet
            </button>
            <b-spinner class="ml-2 align-middle mr-2" v-if="appData.fleetInfo.downloadingTransactionFleet"></b-spinner>
            <button v-if="!isReadOnly" class="btn btn-outline-primary btn-sm" @click.prevent="refreshFleetStatus(false)"
                    :disabled="appData.fleetInfo.downloadingTransactionFleet">
              <i class="fa fa-redo"></i> Refresh Fleet Status
            </button>
          </div>

          <h5 v-if="fleetLookupRunning" class="mt-5">
            <b-spinner class="mr-2 align-middle"></b-spinner>
            <span v-if="appData.fleetInfo.fleetCount && appData.fleetInfo.percentComplete">
              Processing <span class="font-weight-boldest">{{appData.fleetInfo.fleetCount}}</span> vehicles... {{parseInt(appData.fleetInfo.percentComplete)}}% completed.
              <b-progress class="mt-2" v-model="appData.fleetInfo.percentComplete" max="100" height="25px"></b-progress>
            </span>
            <span v-else>
              Fleet processing in progress... please wait.
            </span>
          </h5>

          <div class="mt-5" v-if="!fleetLookupRunning && !fleetExtractionRunning">
            <div v-if="appData.fleetInfo.fleetCount > 0">
              <h6 v-if="appData.fleetInfo.fleetCount > threshold" class="text-dark">
                Due to large fleet size, the vehicle details are not shown below. Please download the fleet details using the button below.
              </h6>

              <div v-if="appData.fleetInfo.incompleteCount > 0" class="mt-5 text-danger">
                <strong>WARNING - {{ appData.fleetInfo.incompleteCount }} {{ appData.fleetInfo.incompleteCount === 1 ? 'vehicle has' : 'vehicles have' }} incomplete information - please review and update.</strong>
              </div>
            </div>
          </div>
        </td>
        <td width="50%" v-if="!isReadOnly && !appData.fleetInfo.downloadingTransactionFleet">
            <fleet-extractor @uploadFleetFiles="uploadFleetFiles" :extraction-job="appData.fleetInfo.fleetExtractionJobInfo"/>
        </td>
      </tr>
      </table>

    <div v-if="showFleetTable" class="mt-10">
        <ValidationObserver ref="vehiclesList">
          <vehicles-list :vehicles="appData.fleetInfo.fleet" :vehicle-types="refData.vehicleTypes"
                         :is-read-only="isReadOnly || !editingFleet"
          />
        </ValidationObserver>

        <div v-if="!isReadOnly" class="mt-5">
          <div v-if="fleetEmpty" class="text-danger">
            At least 1 vehicle is required.
          </div>
          <div v-if="fleetInvalid" class="text-danger">
            Please fix the validation errors in the fleet above.
          </div>

          <button class="btn btn-primary" v-if="!editingFleet" @click.prevent="editFleet">
            <i class="fa fa-edit"></i> Edit Fleet
          </button>
          <button class="btn btn-primary" v-if="editingFleet" @click.prevent="saveFleet">
            <i class="fa fa-save"></i> Save Fleet
          </button>
          <button class="btn btn-secondary ml-5" v-if="editingFleet" @click.prevent="cancelEditMode">
            <i class="fa fa-times"></i> Cancel
          </button>
        </div>
      </div>

  </ValidationObserver>
</template>


<style lang="scss">
</style>

<script>
import _ from 'lodash';
import VehiclesList from "@/view/pages/wizard/components/VehiclesList.vue";
import FleetExtractor from "@/view/pages/wizard/components/FleetExtractor.vue";

export default {
  name: 'FleetPage',
  components: {FleetExtractor, VehiclesList},
  props: {
    appData: Object,
    refData: Object,
    isReadOnly: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      editingFleet: false,
      fleetCopy: null,
      fleetInvalid: false
    };
  },

  mounted() {
    this.refreshFleetStatus(true);
  },

  computed: {
    fleetLookupRunning: function () {
      return this.appData?.fleetInfo?.K_FleetLookupRunning || false;
    },

    fleetExtractionRunning: function () {
      return this.appData?.fleetInfo.K_FleetExtractionRunning || false;
    },

    fleetEmpty: function () {
      return !Array.isArray(this.appData?.fleetInfo?.fleet) || this.appData.fleetInfo.fleet.length === 0;
    },

    threshold: function () {
      return this.appData?.fleetInfo?.fleetUiThreshold;
    },

    showFleetTable: function () {
      return !this.fleetLookupRunning
          && !this.fleetExtractionRunning
          && this.appData.fleetInfo.fleetCount > 0
          && this.appData.fleetInfo.fleetCount <= this.threshold
          && Array.isArray(this.appData.fleetInfo.fleet);
    },

    isRenewalTransaction: function () {
      const transactionType = _.toUpper(this.appData?.transaction?.K_Type || '');
      return transactionType === 'RENEWAL';
    },

    showDeclarationFleetLoadedBanner: function () {
      return this.isRenewalTransaction
          && !this.fleetLookupRunning
          && !this.fleetExtractionRunning
          && Number(this.appData?.fleetInfo?.fleetCount) > 0;
    }
  },

  methods: {
    uploadFleetFiles: function (selectedFiles) {
      this.$emit('uploadFleetFiles', selectedFiles);
    },

    refreshFleetStatus: function (silent) {
      this.$emit('refreshFleetStatus', silent);
    },

    downloadTransactionFleet: function () {
      this.$emit('downloadTransactionFleet');
    },

    editFleet: function () {
      this.fleetInvalid = false;
      this.fleetCopy = _.cloneDeep(this.appData.fleetInfo.fleet);
      this.editingFleet = true;
    },

    saveFleet: async function () {
      const validFleet = await this.$refs.vehiclesList.validate();
      this.fleetInvalid = !validFleet;

      if(this.fleetInvalid || this.fleetEmpty) {
        return;
      }

      this.$emit('saveTransactionFleet', this.appData.fleetInfo.fleet);
      this.fleetCopy = null;
      this.editingFleet = false;
    },

    cancelEditMode: function () {
      this.fleetInvalid = false;
      this.$set(this.appData.fleetInfo, 'fleet', this.fleetCopy);
      this.fleetCopy = null;
      this.editingFleet = false;
    }
  }
};
</script>
